/**
 * UUIDv7 — des identifiants générés côté client, mais ORDONNÉS dans le temps.
 *
 * `crypto.randomUUID()` produit de la v4, purement aléatoire : deux lignes
 * créées à une minute d'intervalle n'ont aucun ordre relatif. L'historique
 * devait donc se trier sur `client_updated_at` — l'horloge que TOUTE écriture
 * réécrit. Corriger le montant d'une dépense la faisait remonter en tête de sa
 * journée, comme si on venait de la saisir.
 *
 * La v7 place l'horodatage de création dans ses 48 premiers bits : l'ordre
 * lexicographique des identifiants EST l'ordre de création, et il ne bouge plus
 * jamais. C'est aussi ce que la convention du projet annonçait déjà.
 *
 * Format (RFC 9562) : 48 bits de millisecondes, 4 bits de version (7),
 * 12 bits aléatoires, 2 bits de variante, 62 bits aléatoires.
 */
export function newId(): string {
  const bytes = crypto.getRandomValues(new Uint8Array(16))

  const ms = Date.now()
  bytes[0] = (ms / 2 ** 40) & 0xff
  bytes[1] = (ms / 2 ** 32) & 0xff
  bytes[2] = (ms / 2 ** 24) & 0xff
  bytes[3] = (ms / 2 ** 16) & 0xff
  bytes[4] = (ms / 2 ** 8) & 0xff
  bytes[5] = ms & 0xff

  // Version 7 sur les 4 bits hauts de l'octet 6, variante RFC sur l'octet 8.
  bytes[6] = (bytes[6] & 0x0f) | 0x70
  bytes[8] = (bytes[8] & 0x3f) | 0x80

  const hex = [...bytes].map((b) => b.toString(16).padStart(2, '0')).join('')
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-` +
         `${hex.slice(16, 20)}-${hex.slice(20)}`
}

/**
 * Un identifiant CALCULÉ, et non tiré au sort — décision 2026-08-24 (soir).
 *
 * Certaines lignes ne sont pas des événements : elles sont *déterminées* par
 * une autre. Une journée prévue ne peut produire qu'UNE dépense. Deux appareils
 * hors ligne qui confirment la même journée doivent donc écrire la MÊME ligne,
 * pas deux.
 *
 * Avec un identifiant aléatoire, chacun en fabriquait un différent : le serveur
 * recevait deux transactions distinctes, les acceptait toutes les deux — il
 * faisait exactement son travail — et la dépense était comptée en double. Sans
 * la moindre erreur, et sans qu'un écran de conflit puisse le voir : deux
 * lignes d'identifiants différents ne sont pas un désaccord.
 *
 * ⚠️ **Les 48 premiers bits de la graine sont conservés.** Un UUIDv7 y porte son
 * horodatage de création, et TOUT le projet s'en sert pour trier
 * chronologiquement — l'historique d'une enveloppe, les opérations d'un jour.
 * Un UUIDv5 classique, entièrement haché, casserait cet ordre en silence.
 *
 * Le reste vient d'un SHA-256 de la graine : même entrée, même sortie, sur
 * n'importe quel appareil. On force ensuite la version 7 et la variante RFC,
 * pour que le résultat reste un UUID valide et se trie comme les autres.
 */
export async function derivedId(seed: string): Promise<string> {
  const digest = new Uint8Array(
    await crypto.subtle.digest('SHA-256', new TextEncoder().encode(seed)))

  const bytes = new Uint8Array(16)
  // L'horodatage de la graine si c'en est une (UUID), sinon celui du hachage —
  // déterministe dans les deux cas.
  const temps = horodatageDe(seed)
  if (temps) bytes.set(temps, 0)
  else bytes.set(digest.subarray(16, 22), 0)

  bytes.set(digest.subarray(0, 10), 6)
  bytes[6] = (bytes[6] & 0x0f) | 0x70
  bytes[8] = (bytes[8] & 0x3f) | 0x80

  const hex = [...bytes].map((b) => b.toString(16).padStart(2, '0')).join('')
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-` +
         `${hex.slice(16, 20)}-${hex.slice(20)}`
}

/** Les 6 octets de temps d'un UUID, ou `null` si la graine n'en est pas un. */
function horodatageDe(seed: string): Uint8Array | null {
  const hex = seed.replace(/-/g, '')
  if (!/^[0-9a-f]{32}$/i.test(hex)) return null
  const bytes = new Uint8Array(6)
  for (let i = 0; i < 6; i++) bytes[i] = parseInt(hex.slice(i * 2, i * 2 + 2), 16)
  return bytes
}
